/**
 * LoginPage — DataVerse.ai sign-in / sign-up, backed by Firebase Auth.
 * Light-themed, reuses the tokens in styles/auth.css (rendered inside .nano-root).
 */
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.2-.1-2.3-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 4.1 29.6 2 24 2 15.6 2 8.3 6.8 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 46c5.5 0 10.5-2.1 14.3-5.5l-6.6-5.6C29.6 36.5 26.9 37.5 24 37.5c-5.2 0-9.6-3.3-11.2-7.9l-6.5 5C8.2 41.1 15.5 46 24 46z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.4l6.6 5.6C41.9 36.3 45 30.8 45 24c0-1.2-.1-2.3-.4-3.5z" />
  </svg>
);

const EyeIcon = ({ off }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {off ? (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    ) : (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    )}
  </svg>
);

export default function LoginPage() {
  const { signIn, signUp, signInWithGoogle } = useAuth();
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    const res = mode === 'signin'
      ? await signIn(email.trim(), password)
      : await signUp(email.trim(), password, name.trim());
    if (res?.error) setError(res.error);
    setBusy(false);
  };

  const google = async () => {
    setError('');
    setBusy(true);
    const res = await signInWithGoogle();
    if (res?.error) setError(res.error);
    setBusy(false);
  };

  return (
    <div className="nano-root" style={{ '--mx': '#818cf8' }}>
      <div className="nano-auth">
        <div className="nano-glow" aria-hidden="true" />
        <div className="nano-auth-card">
          {/* Left hero */}
          <div className="nano-auth-hero">
            <div className="nano-auth-brand">
              <span className="nano-auth-logo">✦</span>
              DataVerse.ai
            </div>
            <h1>One mind.<br />Every way to learn.</h1>
            <p className="nano-auth-tag">
              Solve, quiz, revise, and interview — guided by a single adaptive AI
              that remembers what you know and what to practice next.
            </p>
            <ul className="nano-auth-features">
              <li><span className="nano-auth-fico">🧩</span> 2,800+ DSA problems with an AI tutor</li>
              <li><span className="nano-auth-fico">🎙️</span> Live voice mock interviews</li>
              <li><span className="nano-auth-fico">📈</span> Progress that compounds across topics</li>
            </ul>
          </div>

          {/* Right form */}
          <div className="nano-auth-form">
            <h2>{mode === 'signin' ? 'Welcome back' : 'Create your account'}</h2>
            <p className="nano-auth-sub">
              {mode === 'signin' ? 'Sign in to continue learning.' : 'Start learning in under a minute.'}
            </p>

            <button className="nano-auth-google" onClick={google} disabled={busy} type="button">
              <GoogleIcon /> Continue with Google
            </button>

            <div className="nano-auth-or"><span>or</span></div>

            {error && <div className="nano-auth-banner err">{error}</div>}

            <form onSubmit={submit}>
              {mode === 'signup' && (
                <label className="nano-auth-field">
                  <span>Name</span>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Ada Lovelace" autoComplete="name" />
                </label>
              )}
              <label className="nano-auth-field">
                <span>Email</span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" autoComplete="email" required />
              </label>
              <label className="nano-auth-field nano-auth-pw">
                <span>Password</span>
                <input type={showPw ? 'text' : 'password'} value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'} required />
                <button type="button" className="nano-auth-pwtoggle" onClick={() => setShowPw((s) => !s)}
                  aria-label={showPw ? 'Hide password' : 'Show password'}>
                  <EyeIcon off={showPw} />
                </button>
              </label>

              <button className="nano-auth-submit" disabled={busy} type="submit">
                {busy ? 'Please wait…' : mode === 'signin' ? 'Sign in' : 'Create account'}
              </button>
            </form>

            <div className="nano-auth-switch">
              {mode === 'signin' ? (
                <>New to DataVerse.ai?{' '}
                  <button type="button" onClick={() => { setMode('signup'); setError(''); }}>Create an account</button>
                </>
              ) : (
                <>Already have an account?{' '}
                  <button type="button" onClick={() => { setMode('signin'); setError(''); }}>Sign in</button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
